import {observable, action} from 'mobx'
import {ownRequest} from '../api/ownFetch'
import notificationsStore from './NotificationsStore'
import localizationStore from './LocalizationStore'

/**
 * @typedef AgentCategory
 * @property {number} id
 * @property {number} index
 * @property {string} name
 */

export class Categories {
  @observable.shallow
  list = []

  @action.bound
  setCategories(categories) {
    this.list = categories
  }

  /**
   * @param {AgentCategory} category
   */
  addCategory(category) {
    const index = this.list.findIndex(_cat => _cat.id === category.id)
    if (index === -1) {
      this.list.push(category)
    } else {
      this.list.splice(index, 1, category)
    }
  }

  /**
   * @param {AgentCategory} category
   */
  removeCategory(category) {
    this.list = this.list.filter(_cat => _cat.id !== category.id)
  }
}

export class CategoriesStore {
  categories = new Categories()

  async loadCategories() {
    const categories = await getCategories()
    if (categories) {
      this.categories.setCategories(categories)
    }
  }

  /**
   * @param {string} name
   * @returns {Promise<*>}
   */
  async createCategory(name) {
    const category = await postCategory({name})
    if (category) {
      this.categories.addCategory(category)
      notificationsStore.pushNotification(localizationStore.locale.success)
      return Promise.resolve()
    }
    return error()
  }

  /**
   * @param {AgentCategory} category
   * @param {number|string} indexOrName
   * @returns {Promise<void>}
   */
  async updateCategory(category, indexOrName) {
    if (typeof indexOrName === 'number') {
      const updatedCategory = await updateCategory(category.id, {index: indexOrName})
      if (updatedCategory) {
        this.loadCategories()
      } else {
        return error()
      }
    } else {
      const updatedCategory = await updateCategory(category.id, {name: indexOrName})
      if (updatedCategory) {
        this.categories.addCategory(updatedCategory)
      } else {
        return error()
      }
    }
  }

  /**
   * @param {AgentCategory} category
   * @returns {Promise<void>}
   */
  async deleteCategory(category) {
    const ok = await deleteCategory(category.id)
    if (ok) {
      this.categories.removeCategory(category)
    } else {
      return error()
    }
  }
}

function error() {
  notificationsStore.pushNotification(localizationStore.locale.unknown_error)
  return Promise.reject()
}

/**
 * @params {?number} agentID
 * @returns {Promise<null|Array<AgentCategory>>}
 */
export async function getCategories(agentID) {
  const path = typeof agentID === 'undefined' ? '/agentcategories' : `/agentdata/${agentID}/agentcategories`
  const response = await ownRequest('GET', path, 'application/vnd.uberblik.agentCategory+json;charset:UTF-8')
  if (response.status === 200) {
    /**
     * @type {{agentCategories: Array<AgentCategory>}}
     */
    const data = await response.json()
    return data.agentCategories
  }
  return null
}

/**
 * @param {{name: string}} newCategory
 * @returns {Promise<null|AgentCategory>}
 */
async function postCategory(newCategory) {
  const response = await ownRequest('POST',
    '/agentcategories',
    'application/vnd.uberblik.agentCategory+json;charset:UTF-8',
    undefined,
    {agentCategory: newCategory}
    )
  if (response.ok) {
    /**
     * @type {{agentCategory: AgentCategory}}
     */
    const data = await response.json()
    return data.agentCategory
  }
  return null
}

/**
 * @param {number} categoryID
 * @param {{name?: string, index?: number}} payload
 * @returns {Promise<null|{id: number, name: string, index: number}>}
 */
async function updateCategory(categoryID, payload) {
  const response = await ownRequest('PATCH',
    '/agentcategories/' + categoryID,
    'application/vnd.uberblik.agentCategory+json;charset:UTF-8',
    undefined,
    {agentCategory: payload}
  )
  if (response.ok) {
    /**
     * @type {{agentCategory: AgentCategory}}
     */
    const data = await response.json()
    return data.agentCategory
  }
  return null
}

/**
 * @param {number} categoryID
 * @returns {Promise<boolean>}
 */
async function deleteCategory(categoryID) {
  const response = await ownRequest('DELETE',
    '/agentcategories/' + categoryID
  )
  return response.ok
}

/**
 * @param {AgentCategory} category
 * @param {number} agentID
 * @returns {Promise<boolean>}
 */
export async function addAgentToCategory(category, agentID) {
  const response = await ownRequest(
    'POST',
    `/agentcategories/${category.id}/agentdata/${agentID}`
    )
  return response.ok
}

/**
 * @param {AgentCategory} category
 * @param {number} agentID
 * @returns {Promise<boolean>}
 */
export async function removeAgentFromCategory(category, agentID) {
  const response = await ownRequest(
    'DELETE',
    `/agentcategories/${category.id}/agentdata/${agentID}`
  )
  return response.ok
}

export default Categories



// WEBPACK FOOTER //
// ./src/store/Categories.js